/**
 * Непрочитанное в переписке — для значка в меню.
 *
 * ЗАЧЕМ ОТДЕЛЬНО ОТ ЭКРАНА. Значок нужен на ЛЮБОЙ странице кабинета, а экран
 * переписки открыт в лучшем случае раз в день. Держи счётчик в компоненте — и
 * человек узнавал бы о сообщении, только зайдя туда, где оно и так видно.
 *
 * ОПРОС РЕЖЕ, ЧЕМ В ЧАТЕ. Здесь важно «пришло непрочитанное», а не «пришло
 * прямо сейчас»: полминуты задержки на значке никто не заметит, а нагрузка
 * отличается в шесть раз.
 */
import messenger from '@/utils/messenger';

/** Как часто спрашиваем счётчик, пока вкладка на глазах. */
const POLL_MS = 30000;

let timer = null;
let onVisible = null;

export default {
  namespaced: true,

  state: () => ({
    chatUuid: null,
    unread: 0,
    /** Последний известный хвост — по нему экран понимает, что отстал. */
    lastSeq: 0,
    ready: false,
  }),

  mutations: {
    SET_CHAT(state, { uuid, unread, lastSeq }) {
      state.chatUuid = uuid ?? state.chatUuid;
      state.unread = unread ?? 0;
      state.lastSeq = lastSeq ?? state.lastSeq;
      state.ready = true;
    },
    SET_UNREAD(state, value) {
      state.unread = Math.max(0, value);
    },
    RESET(state) {
      state.chatUuid = null;
      state.unread = 0;
      state.lastSeq = 0;
      state.ready = false;
    },
  },

  actions: {
    /** Спросить состояние разговора. Молча переживает отказ: значок — не повод для тревоги. */
    async refresh({ commit, dispatch }) {
      const res = await messenger.myChat();

      // Токен протух — опрашивать нечего: каждый следующий тик получит тот же
      // отказ, а значок всё равно не обновится.
      if (res.status === 401) {
        dispatch('unwatch');
        return;
      }

      if (!res.ok || !res.data) return;

      // Ответ мог прийти уже ПОСЛЕ выхода: тик ушёл на сервер, человек нажал
      // «выйти», ответ вернулся. Слежение снято — значит счётчик больше не наш.
      if (!timer) return;

      commit('SET_CHAT', {
        uuid: res.data.chat_uuid,
        unread: res.data.unread_count,
        lastSeq: res.data.last_seq,
      });
    },

    /**
     * Начать следить за счётчиком.
     *
     * Только пока вкладка ВИДНА: свёрнутый браузер не должен опрашивать сервер
     * до конца дня. Возвращаемся на вкладку — спрашиваем сразу, не дожидаясь
     * следующего тика, иначе человек полминуты смотрит на устаревший значок.
     */
    watch({ dispatch }) {
      if (timer) return;

      const tick = () => {
        if (document.visibilityState === 'visible') dispatch('refresh');
      };

      tick();
      timer = setInterval(tick, POLL_MS);

      // ССЫЛКУ НА СЛУШАТЕЛЬ ДЕРЖИМ. Снимать его надо тем же значением, иначе
      // он остаётся навсегда: модуль — синглтон, и каждый вход добавлял бы
      // ещё один. После выхода они продолжали бы ходить на сервер с чужим
      // токеном, а следующий вошедший в той же вкладке увидел бы счётчик
      // предыдущего.
      onVisible = tick;
      document.addEventListener('visibilitychange', onVisible);
    },

    unwatch({ commit }) {
      if (timer) clearInterval(timer);
      timer = null;

      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      onVisible = null;

      commit('RESET');
    },

    /** Экран переписки прочитал всё — значок гасим, не дожидаясь опроса. */
    clear({ commit }) {
      commit('SET_UNREAD', 0);
    },
  },
};
